/**
 * 生存曲線（Kaplan-Meier・ログランク）結果を ResultCache に変換するアダプター
 * Phase 4: 既存の survivalAnalysis を呼び出し、右ペインとグラフ用の ResultCache を返す。
 */

import {
  survivalAnalysis,
  type SurvivalAnalysisResult,
  type SurvivalData,
} from '../statistics/survival';
import type { DataRow } from '../../stores/systems/keisuke/dashboardStore';
import { parseFiniteNumberFromCell } from '../keisukeGridData';
import { kt, formatAdapterPValue, KeisukeAdapterError } from '../keisukeAdapterI18n';
import type { AnalysisOptions, ResultCache, VariableMapping } from '../../stores/systems/keisuke/dashboardStore';

function formatPValue(p: number): string {
  if (p < 0.001) return 'p < 0.001';
  if (p < 0.01) return `p = ${p.toFixed(3)}`;
  return `p = ${p.toFixed(2)}`;
}

/** レガシー計助 SurvivalCurveView と同様の論文用 Methods + Results + Figure Legend 英文 */
function buildSurvivalPaperText(result: SurvivalAnalysisResult): string {
  const groups = result.groups;
  const lr = result.logRankTest;
  const pStr = lr.pValue < 0.001 ? '< 0.001' : `= ${lr.pValue.toFixed(3)}`;

  const methodText =
    groups.length === 2
      ? `Survival curves were compared between the ${groups[0].group} (n = ${groups[0].n}) and ${groups[1].group} (n = ${groups[1].n}) groups using the Mantel–Cox log-rank test.`
      : `Survival curves were compared among the ${groups.map((g, i) => (i === groups.length - 1 ? `and ${g.group} (n = ${g.n})` : `${g.group} (n = ${g.n})`)).join(', ')} groups using the Mantel–Cox log-rank test.`;
  const methodTextFull =
    methodText +
    (result.multipleComparisons && result.multipleComparisons.length > 0
      ? ' Pairwise comparisons were performed with Bonferroni correction.'
      : '');

  // ANOVA と同様に Results 冒頭で群要約（n, events, median survival）を記載
  const groupSummaryLine = groups
    .map((g) => {
      const med =
        g.medianSurvival !== undefined
          ? `${g.medianSurvival.toFixed(1)} days`
          : 'not reached';
      return `${g.group} (n = ${g.n}, events = ${g.events}, median survival: ${med})`;
    })
    .join('; ') + '.';

  const resultsTextFirst =
    groupSummaryLine +
    ' ' +
    (groups.length === 2
      ? lr.pValue < 0.05
        ? `The Mantel–Cox log-rank test revealed a ${lr.pValue < 0.001 ? 'highly ' : ''}significant difference in survival between the ${groups[0].group} and ${groups[1].group} groups (χ² = ${lr.chiSquare.toFixed(2)}, df = ${lr.df}, P ${pStr}).\n\n`
        : `The Mantel–Cox log-rank test revealed no significant difference in survival between the ${groups[0].group} and ${groups[1].group} groups (χ² = ${lr.chiSquare.toFixed(2)}, df = ${lr.df}, P = ${lr.pValue.toFixed(3)}).\n\n`
      : lr.pValue < 0.05
        ? `The Mantel–Cox log-rank test revealed a ${lr.pValue < 0.001 ? 'highly ' : ''}significant difference in survival among the ${groups.length === 3 ? 'three' : groups.length.toString()} groups (χ² = ${lr.chiSquare.toFixed(2)}, df = ${lr.df}, P ${pStr}).\n\n`
        : `The Mantel–Cox log-rank test revealed no significant difference in survival among the ${groups.length === 3 ? 'three' : groups.length.toString()} groups (χ² = ${lr.chiSquare.toFixed(2)}, df = ${lr.df}, P = ${lr.pValue.toFixed(3)}).\n\n`);

  const resultsTextGroups = groups
    .map((group, index) => {
      const medianText =
        group.medianSurvival !== undefined
          ? `${group.medianSurvival.toFixed(1)} days`
          : 'not reached during the observation period';
      const prefix = 'The ';
      const suffix = index < groups.length - 1 ? '. ' : '.';
      const eventText = group.events === 1 ? 'event' : 'events';
      return `${prefix}${group.group} group had ${group.events} ${eventText} among ${group.n} subjects${group.medianSurvival !== undefined ? `, with a median survival time of ${medianText}` : `, and the median survival time was ${medianText}`}${suffix}`;
    })
    .join('\n\n');

  let resultsTextComparisons = '';
  if (result.multipleComparisons && result.multipleComparisons.length > 0) {
    const significant: typeof result.multipleComparisons = [];
    const notSignificant: typeof result.multipleComparisons = [];
    result.multipleComparisons.forEach((comp) => {
      const p = comp.adjustedP !== undefined ? comp.adjustedP : comp.pValue;
      if (p < 0.05) significant.push(comp);
      else notSignificant.push(comp);
    });
    const significantText =
      significant.length > 0
        ? significant
            .map((comp, index) => {
              const p = comp.adjustedP !== undefined ? comp.adjustedP : comp.pValue;
              const pText = p < 0.001 ? 'P < 0.001' : `P = ${p.toFixed(3)}`;
              const comparisonText = comp.comparison.replace(' vs ', ' and ');
              if (index === 0) {
                return `a significant difference between the ${comparisonText} groups (${pText})`;
              }
              return `and between the ${comparisonText} groups (${pText})`;
            })
            .join(' ')
        : '';
    const notSignificantText =
      notSignificant.length > 0
        ? notSignificant
            .map((comp, index) => {
              const p = comp.adjustedP !== undefined ? comp.adjustedP : comp.pValue;
              const pText = p < 0.001 ? 'P < 0.001' : `P = ${p.toFixed(3)}`;
              const comparisonText = comp.comparison.replace(' vs ', ' and ');
              if (index === 0) {
                return `between the ${comparisonText} groups (${pText})`;
              }
              return `, the ${comparisonText} groups (${pText})`;
            })
            .join('')
        : '';
    if (significant.length > 0 && notSignificant.length > 0) {
      resultsTextComparisons = `\n\nPairwise comparisons with Bonferroni correction showed ${significantText}, whereas no significant difference was observed ${notSignificantText}.`;
    } else if (significant.length > 0) {
      resultsTextComparisons = `\n\nPairwise comparisons with Bonferroni correction showed ${significantText}.`;
    } else if (notSignificant.length > 0) {
      resultsTextComparisons = `\n\nPairwise comparisons with Bonferroni correction showed no significant difference ${notSignificantText}.`;
    }
  }

  const figureText =
    `Figure X. Kaplan–Meier survival curves for the ${groups.map((g, i) => (i === groups.length - 1 ? `and ${g.group}` : g.group)).join(', ')} groups. ` +
    groups
      .map((group, index) => {
        const medianText =
          group.medianSurvival !== undefined
            ? `median survival: ${group.medianSurvival.toFixed(1)} days`
            : 'median survival: not reached';
        return `${group.group} (n = ${group.n}, events = ${group.events}, ${medianText})${index < groups.length - 1 ? ', ' : '. '}`;
      })
      .join('') +
    `Survival curves were compared using the Mantel–Cox log-rank test (χ² = ${lr.chiSquare.toFixed(2)}, df = ${lr.df}, P ${pStr}). ` +
    (result.multipleComparisons && result.multipleComparisons.length > 0
      ? 'Pairwise comparisons were performed with Bonferroni correction. '
      : '') +
    'Tick marks indicate censored observations.';

  return `Methods\n\n${methodTextFull}\n\nResults\n\n${resultsTextFirst}${resultsTextGroups}${resultsTextComparisons}\n\nFigure Legend\n\n${figureText}`;
}

export function runSurvivalAdapter(
  gridData: DataRow[],
  options: AnalysisOptions
): ResultCache {
  const mapping: VariableMapping = options.mapping ?? {};
  const groupCol = mapping.survivalGroupColumn ?? mapping.groupColumn;
  const timeCol = mapping.timeColumn;
  const eventCol = mapping.eventColumn;

  if (!groupCol) throw new KeisukeAdapterError('groupColumnRequired');
  if (!timeCol) throw new KeisukeAdapterError('timeColumnRequired');
  if (!eventCol) throw new KeisukeAdapterError('eventColumnRequired');
  if (gridData.length === 0) throw new KeisukeAdapterError('dataRequired');

  const data: SurvivalData[] = [];
  for (const row of gridData) {
    const ev = row[eventCol];
    const timeNum = parseFiniteNumberFromCell(row[timeCol]);
    if (timeNum === null || timeNum < 0) continue;
    let eventBool: boolean;
    if (typeof ev === 'boolean') eventBool = ev;
    else if (typeof ev === 'number') eventBool = ev !== 0;
    else eventBool = String(ev).toLowerCase() === '1' || String(ev).toLowerCase() === 'true' || String(ev) === '1';
    data.push({
      time: timeNum,
      event: eventBool,
      group: String(row[groupCol] ?? ''),
    });
  }

  if (data.length === 0) throw new KeisukeAdapterError('validDataRequired');
  const result = survivalAnalysis(data, true);

  const stats: Record<string, unknown> = {
    'Log-rank χ²': Math.round(result.logRankTest.chiSquare * 1000) / 1000,
    'Log-rank p': result.logRankTest.pValue,
    df: result.logRankTest.df,
  };
  result.groups.forEach((g) => {
    stats[`${g.group} (n)`] = g.n;
    stats[`${g.group} (events)`] = g.events;
    if (g.medianSurvival != null) stats[`${g.group} (median survival)`] = Math.round(g.medianSurvival * 100) / 100;
  });

  const survivalChart = result.groups.map((g) => ({
    group: g.group,
    points: g.curve.map((p) => ({ time: p.time, survival: p.survival * 100 })),
  }));

  const lr = result.logRankTest;
  const statsDetailText = [
    kt('statsDetail.overviewTitle'),
    kt('statsDetail.survival.intro'),
    kt('statsDetail.survival.logRankLine', {
      chi: lr.chiSquare.toFixed(3),
      df: lr.df,
      p: formatAdapterPValue(lr.pValue),
    }),
    result.multipleComparisons?.length ? kt('statsDetail.survival.multipleComparisonsNote') : '',
    kt('statsDetail.formulasRef'),
  ]
    .filter(Boolean)
    .join('\n');

  return {
    stats,
    paperText: buildSurvivalPaperText(result),
    statsDetailText,
    survivalChart,
    rawResult: result,
    updatedAt: Date.now(),
  };
}
